/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/forms/form/Form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from '@/components/forms/inputs/select/Select'
import { inputVariants } from '@/components/forms/inputs/text/Input'
import useIsMobile from '@/hooks/useIsMobile'
import { cn } from '@/utils'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

const googleApiKey = import.meta.env.VITE_GOOGLE_FONT as string

interface GoogleFont {
  family: string
  category: string
  variants: string[]
  subsets: string[]
  version: string
  lastModified: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function FontPicker({ form }: { form: any }) {
  const [fonts, setFonts] = useState<GoogleFont[]>([])
  const [fontValue, setFontValue] = useState('')
  const [itemsToShow, setItemsToShow] = useState(100)
  // Search states
  const [searchResults, setSearchResults] = useState<GoogleFont[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const { register, watch } = useForm({
    defaultValues: {
      searchQuery: ''
    }
  })
  const searchQuery = watch('searchQuery')
  const isMobile = useIsMobile(768)

  useEffect(() => {
    if (!searchQuery) {
      setSearchResults([])
      if (isSearching) setIsSearching(false)
      return
    }
    setIsSearching(true)
    const debounceTimeout = setTimeout(() => {
      const results = fonts.filter((font) =>
        font.family.toLowerCase().includes(searchQuery.toLowerCase())
      )
      setSearchResults(results)
      setIsSearching(false)
    }, 800)

    return () => clearTimeout(debounceTimeout)
  }, [searchQuery])

  // Fetch fonts from the Google Fonts API on component mount
  useEffect(() => {
    const fetchFonts = async () => {
      try {
        const response = await fetch(
          `https://www.googleapis.com/webfonts/v1/webfonts?sort=POPULARITY&key=${googleApiKey}`
        )
        const data = (await response.json()) as { items: GoogleFont[] }
        if (response.ok && data?.items) {
          setFonts(data.items)
        }
      } catch (error) {
        console.error('Error fetching Google Fonts:', error)
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchFonts()
  }, [])

  // Function to handle changes and generate link tag string
  const getLinkString = useCallback((value: string) => {
    const slicedValue = value.split('_')[1]
    // Format the font family for the URL (replace spaces with '+')
    const formattedFont = slicedValue.replace(/\s/g, '+')
    const linkString = `<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=${formattedFont}&display=swap" rel="stylesheet">`
    return linkString
  }, [])

  return (
    <>
      <FormField
        control={form.control}
        name="preferredFontHeadHtml"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Wybierz czcionkę</FormLabel>
            <Select
              onValueChange={(value) => {
                const linkString = getLinkString(value)
                field.onChange(linkString)
                setFontValue(value)
              }}
              required
              value={fontValue}
            >
              <FormControl>
                <SelectTrigger>
                  {fontValue ? (
                    <span
                      style={{
                        fontFamily: `${fontValue.split('_')[1].replace(/_/g, ' ')}, sans-serif`
                      }}
                    >
                      {fontValue.split('_')[1].replace(/_/g, ' ')}
                    </span>
                  ) : (
                    'Wybierz czcionkę'
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent
                align="center"
                className=""
                side={isMobile ? 'top' : 'bottom'}
              >
                <div className="p-2">
                  <input
                    className={cn(inputVariants({ variant: 'default' }))}
                    onKeyDown={(e) => {
                      e.stopPropagation()
                    }}
                    placeholder="Wpisz wyszukiwaną czcionkę"
                    type="text"
                    {...register('searchQuery')}
                  />
                </div>

                {/* Separator line above the normal list */}

                <div className="max-h-96 overflow-y-auto">
                  <div className="p-2 text-sm font-semibold">Szukane czcionki</div>
                  <>
                    {isSearching ? (
                      <div className="p-2 text-center text-sm text-gray-500">
                        Wczytywanie...
                      </div>
                    ) : searchResults.length > 0 ? (
                      searchResults.map((font) => (
                        <SelectItem
                          key={`${font.family}_searched${font.family}`}
                          value={`SEARCH_${font.family}`}
                        >
                          <span style={{ fontFamily: `${font.family}, sans-serif` }}>
                            {font.family}
                          </span>
                        </SelectItem>
                      ))
                    ) : (
                      <div className="p-2 text-center text-sm text-gray-500">
                        Brak wyników
                      </div>
                    )}
                  </>
                  <div className="border-t border-border p-2 text-sm font-semibold">
                    Wszystkie czcionki
                  </div>
                  <>
                    {fonts.slice(0, itemsToShow).map((font) => (
                      <SelectItem key={`${font.family}_all`} value={`ALL_${font.family}`}>
                        <span style={{ fontFamily: `${font.family}, sans-serif` }}>
                          {font.family}
                        </span>
                      </SelectItem>
                    ))}
                    {/* Load More Button */}
                    {fonts.length > itemsToShow && (
                      <button
                        className="w-full cursor-pointer rounded-md bg-background p-2 text-primary"
                        onClick={() => setItemsToShow(itemsToShow + 100)}
                      >
                        Załaduj więcej
                      </button>
                    )}
                  </>
                </div>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}
